import { useState, useEffect } from 'react';

type CalibratedPair = [number, string]; // screen dimension in px, target value with unit

export const plainTableDefaults: CalibratedPair[] = [
	[753, '74vh'],
	[1048, '81vh'],
];

export const useResponsiveSpace = (
	calibration: CalibratedPair[],
	isWidth = false,
): string => {
	const [result, setResult] = useState(calibration[0][1]);

	useEffect(() => {
		const parseValue = (str: string): number => {
			const match = str.match(/^(\d+(?:\.\d+)?)/);
			return match ? Number.parseFloat(match[1]) : 0;
		};

		const getUnit = (str: string): string => {
			const match = str.match(/[a-zA-Z%]+$/);
			return match ? match[0] : '';
		};

		const sorted = [...calibration].sort((a, b) => a[0] - b[0]);

		const update = () => {
			const screenSize = isWidth ? window.innerWidth : window.innerHeight;

			for (let i = 0; i < sorted.length - 1; i++) {
				const [s1, v1] = sorted[i];
				const [s2, v2] = sorted[i + 1];
				const unit = getUnit(v1);

				if (getUnit(v2) !== unit) {
					throw new Error(`Inconsistent units in calibration: ${v1} and ${v2}`);
				}

				if (screenSize >= s1 && screenSize <= s2) {
					const ratio = (screenSize - s1) / (s2 - s1);
					const val1 = parseValue(v1);
					const val2 = parseValue(v2);
					const interpolated = val1 + (val2 - val1) * ratio;
					setResult(`${interpolated.toFixed(2)}${unit}`);
					return;
				}
			}

			const [minS, minV] = sorted[0];
			const [maxS, maxV] = sorted[sorted.length - 1];
			setResult(screenSize < minS ? minV : maxV);
		};

		update(); // Initial call

		window.addEventListener('resize', update);
		return () => {
			window.removeEventListener('resize', update);
		};
	}, [calibration, isWidth]);

	return result;
};
